import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatTimeAgo } from "@/shared/lib/date-time";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  confirm,
  Textarea,
} from "@devhop/ui";
import {
  Link,
  useNavigate,
  useParams,
  useRouter,
} from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  EyeOffIcon,
  FileTextIcon,
  InfoIcon,
  PackageIcon,
  RotateCcwIcon,
  StoreIcon,
  TrashIcon,
} from "lucide-react";
import { useState } from "react";
import type { ListingDetail } from "../api/client";
import { useBulkUpdateListingsMutation, useListingQuery } from "../api/queries";
import { ListingPriceSection } from "../ui/ListingPriceSection";

const STATUS_LABELS: Record<string, string> = {
  active: "ໃຊ້ງານ",
  hidden: "ເຊື່ອງ",
  removed: "ລຶບແລ້ວ",
};

function ListingDetailContent({ listing }: { listing: ListingDetail }) {
  const nav = useNavigate({ from: "/app/moderation/listings/$id" });
  const router = useRouter();
  const bulkUpdate = useBulkUpdateListingsMutation();
  const [reason, setReason] = useState("");

  const statusLabel = STATUS_LABELS[listing.status] ?? listing.status;
  const imageSrc = listing.imageUrl ? resolveImageSrc(listing.imageUrl) : null;

  const handleHide = async () => {
    const ok = await confirm({
      title: "ເຊື່ອງລາຍການສິນຄ້າ",
      description: reason.trim()
        ? `ເຫດຜົນ: ${reason.trim()}\n\nທ່ານແນ່ໃຈບໍ່ວ່າຈະເຊື່ອງລາຍການນີ້? ລາຍການທີ່ເຊື່ອງຈະບໍ່ສະແດງບນ marketplace ແຕ່ສາມາດເປີດຄືນໄດ້.`
        : "ທ່ານແນ່ໃຈບໍ່ວ່າຈະເຊື່ອງລາຍການນີ້? ລາຍການທີ່ເຊື່ອງຈະບໍ່ສະແດງບນ marketplace ແຕ່ສາມາດເປີດຄືນໄດ້.",
      actionText: "ເຊື່ອງ",
      ActionProps: { variant: "default" },
    });
    if (!ok) return;
    await bulkUpdate.mutateAsync({
      ids: [listing.id],
      status: "hidden",
      reason: reason.trim() || undefined,
    });
    nav({ to: "/app/moderation/listings/$id" });
  };

  const handleRestore = async () => {
    const ok = await confirm({
      title: "ເປີດຄືນລາຍການສິນຄ້າ",
      description: reason.trim()
        ? `ເຫດຜົນ: ${reason.trim()}\n\nທ່ານແນ່ໃຈບໍ່ວ່າຈະເປີດຄືນລາຍການນີ້?`
        : "ທ່ານແນ່ໃຈບໍ່ວ່າຈະເປີດຄືນລາຍການນີ້?",
      actionText: "ເປີດຄືນ",
      ActionProps: { variant: "default" },
    });
    if (!ok) return;
    await bulkUpdate.mutateAsync({
      ids: [listing.id],
      status: "active",
      reason: reason.trim() || undefined,
    });
    nav({ to: "/app/moderation/listings/$id" });
  };

  const handleRemove = async () => {
    const ok = await confirm({
      title: "ລຶບລາຍການສິນຄ້າ",
      description: reason.trim()
        ? `ເຫດຜົນ: ${reason.trim()}\n\nທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບລາຍການນີ້? ການລຶບເປັນການຖືກຖອນຖາວອນ ລາຍການຈະບໍ່ສະແດງອີກ.`
        : "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບລາຍການນີ້? ການລຶບເປັນການຖືກຖອນຖາວອນ ລາຍການຈະບໍ່ສະແດງອີກ.",
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    await bulkUpdate.mutateAsync({
      ids: [listing.id],
      status: "removed",
      reason: reason.trim() || undefined,
    });
    nav({ to: "/app/moderation/listings/$id" });
  };

  return (
    <>
      {/* Product header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant={
                listing.status === "active"
                  ? "success"
                  : listing.status === "removed"
                    ? "destructive"
                    : "secondary"
              }
            >
              {statusLabel}
            </Badge>
            <span className="text-muted-foreground text-sm">
              ID: {listing.id.slice(0, 8).toUpperCase()}
            </span>
          </div>
          <h1 className="font-bold text-2xl tracking-tight">
            {listing.productName}
          </h1>
          <p className="text-muted-foreground text-sm">
            ສົ່ງເມື່ອ {formatTimeAgo(listing.createdAt)} ໂດຍ{" "}
            <Link
              to="/app/shops/$id"
              params={{ id: listing.shopId }}
              className="text-primary hover:underline"
            >
              {listing.shopName}
            </Link>
          </p>
        </div>

        <div className="flex justify-end">
          <Button onClick={() => router.history.back()} variant="outline">
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        {/* ซ้าย: ข้อมูลสินค้า */}
        <div className="space-y-6">
          {/* รูปสินค้า */}
          <Card className="p-0">
            <CardContent className="p-0">
              <div className="relative aspect-square max-h-[400px] w-full overflow-hidden rounded-t-lg bg-muted">
                {imageSrc ? (
                  <img
                    src={imageSrc}
                    alt={listing.productName}
                    className="size-full object-contain"
                  />
                ) : (
                  <div className="flex size-full items-center justify-center">
                    <PackageIcon className="size-16 text-muted-foreground" />
                  </div>
                )}
                {imageSrc && (
                  <Badge
                    variant="secondary"
                    className="absolute top-2 right-2 text-xs"
                  >
                    ຮູບຫຼັກ
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* คำอธิบายสินค้า */}
          <Card className="gap-0">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <FileTextIcon className="size-4" aria-hidden />
                ຄຳອະທິບາຍສິນຄ້າ
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {listing.productDescription ? (
                <p className="whitespace-pre-line text-muted-foreground text-sm leading-relaxed">
                  {listing.productDescription}
                </p>
              ) : (
                <p className="text-muted-foreground text-sm">ບໍ່ມີຄຳອະທິບາຍ</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ขวา: รายละเอียด + การดำเนินการ + Quick analysis */}
        <div className="space-y-6">
          {/* Marketplace price */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">ລາຄາໃນ Marketplace</CardTitle>
            </CardHeader>
            <CardContent>
              <ListingPriceSection listing={listing} />
            </CardContent>
          </Card>

          {/* Listing details */}
          <Card>
            <CardContent>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div>
                  <dt className="font-medium text-muted-foreground">ຫມວດຫມູ່</dt>
                  <dd>{listing.categoryName ?? "—"}</dd>
                </div>
                <div>
                  <dt className="font-medium text-muted-foreground">ຮ້ານຄ້າ</dt>
                  <dd>
                    <Link
                      to="/app/shops/$id"
                      params={{ id: listing.shopId }}
                      className="text-primary hover:underline"
                    >
                      {listing.shopName}
                    </Link>
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-muted-foreground">SKU</dt>
                  <dd>{listing.sku}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          {/* Moderation actions */}
          <Card className="gap-0">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <StoreIcon className="size-4" aria-hidden />
                ການດຳເນີນການກວດກາ
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label
                  htmlFor="mod-reason"
                  className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide"
                >
                  ເຫດຜົນການກວດກາ
                </label>
                <Textarea
                  id="mod-reason"
                  placeholder="ກະລຸນາກອງເຫດຜົນ (ຕົວຢ່າງ: ພົບສິນຄ້າລອກ, ຮູບບໍ່ເໝາະສົມ, ລາຍການຊ້ຳ...)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="resize-none"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {listing.status === "active" && (
                  <Button
                    className="w-full flex-1"
                    onClick={handleHide}
                    disabled={bulkUpdate.isPending}
                  >
                    <EyeOffIcon className="size-4" />
                    ເຊື່ອງລາຍການ
                  </Button>
                )}
                <div className="flex w-full gap-2">
                  {listing.status !== "active" && (
                    <Button
                      className="w-full flex-1"
                      variant="outline"
                      onClick={handleRestore}
                      disabled={bulkUpdate.isPending}
                    >
                      <RotateCcwIcon className="size-4" />
                      ເປີດຄືນ
                    </Button>
                  )}
                  {listing.status !== "removed" && (
                    <Button
                      className="w-full flex-1"
                      variant="destructive"
                      onClick={handleRemove}
                      disabled={bulkUpdate.isPending}
                    >
                      <TrashIcon className="size-4" />
                      ລຶບ
                    </Button>
                  )}
                </div>
              </div>
              <Alert
                variant="default"
                className="border-amber-500/50 bg-amber-500/10"
              >
                <InfoIcon className="size-4" />
                <AlertDescription>
                  ການລຶບລາຍການເປັນການຖືກຖອນຖາວອນ.
                  ລາຍການທີ່ເຊື່ອງສາມາດເປີດຄືນໄດ້ຫຼັງຮ້ານແກ້ຕາມທີ່ກວດກາແນະນຳ.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Quick analysis (placeholder) */}
          {/* <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">ການວິເຄາະສັ້ນ</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">ຄວາມເບ່ຍີ່ງລາຄາ</dt>
                  <dd>—</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">ຄະແນນຮ້ານ</dt>
                  <dd>—</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">ຄວາມເປັນເອກະລາດຮູບ</dt>
                  <dd>—</dd>
                </div>
              </dl>
            </CardContent>
          </Card> */}
        </div>
      </div>
    </>
  );
}

export function ListingDetailPage() {
  const { id } = useParams({ from: "/app/moderation/listings/$id" });
  const { data: listing, isLoading } = useListingQuery(id ?? "");

  if (isLoading || !listing) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: "ການກວດກາ Marketplace", to: "/app/moderation" },
              { label: "ລາຍລະອຽດ" },
            ]}
          />
        </Header>
        <Main>
          <div className="text-muted-foreground">
            {isLoading ? "ກຳລັງໂຫຼດ..." : "ບໍ່ພົບລາຍການ"}
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ການກວດກາ Marketplace", to: "/app/moderation" },
            { label: "ລາຍລະອຽດລາຍການ" },
          ]}
        />
      </Header>

      <Main>
        <ListingDetailContent listing={listing} />
      </Main>
    </>
  );
}
